
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  * 
  * The source file is located at:
  *   /codebuild/output/src2984367493/src/vl-app/pages/patients/todos.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import CaseMenu from '@components/Filter/CaseMenu';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import ToDoTable from '@components/Patients/ToDos/ToDoTable';
import { Button, Spin } from 'antd';
import { ArrowRightOutlined } from 'antd/icons';
import styled from '@emotion/styled';
import useAuth from '@vl-core/useAuth';
import NeedHelp from '@components/Patients/Welcome/NeedHelp';
import PatientMediaPermissionsWarning from '@components/MediaPermissions/PatientMediaPermissionsWarning';
import { PatientPageHeading } from '@components/Layouts/PageHead/PatientPageHeading';
import { css } from '@emotion/react';
import TypographyExpandEllipsis from 'vl-common/src/components/UI/ExpandEllipsis';
import { useTranslation } from 'react-i18next';
import useIsNHS from '@hooks/useIsNHS';
import Header from '@components/Layouts/Header/Header';

const PatientsTodos = () => {
  const initialised = useInitialisedStore();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const isNHS = useIsNHS();

  const noSelfReferString = isNHS
    ? t('If you believe you have anything outstanding, please contact support')
    : t('To get help with a new condition please contact AXA Health');

  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(
    sessionStorage.getItem('case-filter-selected') || null
  );
  const [selectedCaseData, setSelectedCaseData] = useState<any>({});
  const [newCaseCreated, setNewCaseCreated] = useState('');
  const [allTodosComplete, setAllTodosComplete] = useState(false);
  const [getSpecialistOpinion, setGetSpecialistOpinion] = useState(false);

  const canSelfRefer = user?.self_refer && user?.insured;

  const { data: todoList = { todo: [] }, isInitialLoading } = useQuery({
    enabled: user?.user_role_code === 'PATIENT' && !!selectedCaseId,
    queryKey: ['todoList', selectedCaseId],
    queryFn: () => dispatch(actions.getToDoList(selectedCaseId || undefined))
  });

  const { data: { surveyjs_defs } = { surveyjs_defs: [] } } = useQuery({
    queryKey: ['useSurvey'],
    queryFn: () => dispatch(actions.getSurveyJsDefinition({ cat_code: 'UI', event_code: 'SR', current: 'true' }))
  });
  const noSurveyDefinition = surveyjs_defs?.length > 0;

  useEffect(() => {
    queryClient
      .invalidateQueries({
        queryKey: ['todoList']
      })
      .then();
  }, [queryClient]);

  const refetchTodo = () => {
    queryClient
      .invalidateQueries({
        queryKey: ['todoList', selectedCaseId]
      })
      .then();
  };

  useEffect(() => {
    const completedTodos = todoList.todo?.filter(
      (item) => item.status === 'Complete' || item.status === 'Complete (Editable)'
    );
    if (completedTodos) {
      setAllTodosComplete(completedTodos.length === todoList.todo?.length);
    } else {
      setAllTodosComplete(true);
    }
  }, [todoList]);

  const StyledHelpContainer = styled.div`
    width: 20%;
    @media (max-width: 770px) {
      width: 100%;
    }
  `;
  const StyledTodoContainer = styled.div`
    width: 70%;
    @media (max-width: 770px) {
      width: 100%;
      padding-bottom: 30px;
    }
  `;

  if (!initialised) {
    return <p>{t('Initialising ...')}</p>;
  }

  return (
    <div className="page-container">
      <Header pageType="patients" pageTitle="To-do list" />
      <section className="section-layout patient">
        <main
          className="container"
          id="page-content"
          style={{ display: 'flex', gap: '8%', flexWrap: 'wrap', flexDirection: 'row' }}
        >
          <PatientMediaPermissionsWarning />
          <StyledTodoContainer>
            <PatientPageHeading>{t('Next steps')}</PatientPageHeading>
            <TypographyExpandEllipsis
              maxRows={2}
              paragraphText={t("Here you can find all your next steps. Select what you'd like to do next.")}
            />
            <CaseMenu
              selectedCaseId={selectedCaseId}
              setSelectedCaseId={setSelectedCaseId}
              newCaseCreated={newCaseCreated}
              setNewCaseCreated={setNewCaseCreated}
              setSelectedCaseData={setSelectedCaseData}
              createNewCase={getSpecialistOpinion}
              setCreateNewCase={setGetSpecialistOpinion}
            />
            {isInitialLoading && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  height: 80px;
                `}
              >
                <Spin />
              </div>
            )}
            {selectedCaseId && !isInitialLoading && (
              <ToDoTable
                todoList={todoList?.todo}
                caseData={selectedCaseData}
                allTodosComplete={allTodosComplete}
                refetch={refetchTodo}
              />
            )}
            {!selectedCaseId && !isInitialLoading && (
              <div>
                <h2
                  style={{
                    paddingTop: '30px',
                    fontSize: '20pxs',
                    fontWeight: '600'
                  }}
                >
                  {t('New condition')}
                </h2>
                <div style={{ paddingBottom: '20px' }}>
                  {canSelfRefer && noSurveyDefinition
                    ? t('To get help with a new condition, please click below:')
                    : noSelfReferString}
                </div>
                {canSelfRefer && noSurveyDefinition && (
                  <Button
                    type="primary"
                    className="button"
                    style={{ height: '46px', fontSize: '16px', fontWeight: '600' }}
                    data-testid="get-specialist-opinion"
                    onClick={() => setGetSpecialistOpinion(true)}
                  >
                    {t('Get expert help for a new condition')} <ArrowRightOutlined />
                  </Button>
                )}
              </div>
            )}
          </StyledTodoContainer>
          <StyledHelpContainer>
            <NeedHelp />
          </StyledHelpContainer>
        </main>
      </section>
    </div>
  );
};

export default PatientsTodos;
export const Route = createFileRoute('/patients/todos')({ component: PatientsTodos });
